import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { getTargetProgress } from '../../api/targets';
import { TargetMetricProgress } from '../../types/target';

interface TargetProgressDialogProps {
  targetId: number | null;
  onClose: () => void;
}

const MetricRow = ({ label, metric }: { label: string; metric: TargetMetricProgress }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="body2" color="text.secondary">
        {metric.actual}
        {metric.target !== null ? ` / ${metric.target}` : ' (no target set)'}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(metric.percent ?? 0, 100)}
      color={metric.percent !== null && metric.percent >= 100 ? 'success' : 'primary'}
    />
  </Box>
);

export const TargetProgressDialog = ({ targetId, onClose }: TargetProgressDialogProps) => {
  const progressQuery = useQuery({
    queryKey: ['target-progress', targetId],
    queryFn: () => getTargetProgress(targetId!),
    enabled: targetId !== null,
  });

  const data = progressQuery.data;

  return (
    <Dialog open={targetId !== null} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {data ? `${data.target.user.firstName} ${data.target.user.lastName} — progress` : 'Progress'}
      </DialogTitle>
      <DialogContent>
        {!data ? (
          <Typography variant="body2" color="text.secondary">
            {progressQuery.isLoading ? 'Loading…' : 'No data.'}
          </Typography>
        ) : (
          <Stack spacing={2.5} sx={{ pb: 1 }}>
            <MetricRow label="Leads assigned" metric={data.progress.leads} />
            <MetricRow label="Registrations" metric={data.progress.registrations} />
            <MetricRow label="Admissions" metric={data.progress.admissions} />
            <MetricRow label="Approved payments" metric={data.progress.payments} />
            <Typography variant="caption" color="text.secondary">
              {data.progress.leadsConverted} of this period's leads have converted to students.
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
